/**
 * Extracts a human-readable error message from an Axios error.
 *
 * Handles all error shapes returned by this API:
 *   { error: "..." }                          — most controller error responses
 *   { message: "..." }                        — auth and some other endpoints
 *   { title: "...", errors: { F: ["msg"] } }  — ASP.NET Core model validation
 *   { title: "..." }                          — ASP.NET Core problem details
 *   plain string body                         — uncommon
 *   no response (network error)               — err.message fallback
 *
 * @param {unknown} err - The caught error (typically an Axios error)
 * @param {string} [fallback] - Message to return when nothing else can be extracted
 * @returns {string}
 */
// Friendly explanations for the IVR /initiate block reason codes (HTTP 422).
// Keep keys in sync with IvrCallInitiationResponse.BlockedByCompanySettings on the server.
export const IVR_BLOCK_REASONS = {
  CompanyIvrDisabled: 'The patient’s company has IVR turned off (bIVRCompany = false).',
  CompanyIvrPaused: 'The patient’s company has IVR paused (bPauseIVR = true).',
  PatientOnHold: 'The patient is on outreach hold.',
  PatientDoNotCall: 'The patient is flagged do-not-call.',
  PatientDoNotContact: 'The patient’s form of contact is set to "Do Not Contact".',
  CompanyCgmInactive: 'The patient’s company has no active CGM product line.',
  PatientNotCgmOnly: 'The CGM IVR only calls patients whose only product line is CGM.',
  PatientNumberAlreadyCalledToday:
    'This patient + number was already IVR-called today (once-per-day guard). Use the testing override to bypass on dev/qa.',
};

/**
 * If the error is a blocked (422) IVR initiate response, returns a clear message
 * naming the reason; otherwise returns null.
 * @param {unknown} err - The caught Axios error.
 * @returns {string|null}
 */
export function parseIvrBlockedMessage(err) {
  const data = err?.response?.data;
  if (!data?.blocked || !data?.blockedReason) return null;
  const detail = IVR_BLOCK_REASONS[data.blockedReason] || 'The call was blocked by a server-side rule.';
  return `Call blocked: ${data.blockedReason} — ${detail}`;
}

export function parseApiError(err, fallback = 'An unexpected error occurred.') {
  const data = err?.response?.data;

  if (!data) return err?.message || fallback;

  if (typeof data === 'string') return data;

  // { error: "..." } — our standard controller error shape
  if (typeof data.error === 'string') return data.error;

  // { message: "..." } — auth / other endpoints
  if (typeof data.message === 'string') return data.message;

  // ASP.NET Core model validation: { title, errors: { Field: ["msg", ...] } }
  if (data.errors && typeof data.errors === 'object') {
    const fieldMessages = Object.values(data.errors).flat().filter(Boolean);
    if (fieldMessages.length > 0) return fieldMessages.join(' ');
  }

  // ASP.NET Core problem details: { title }
  if (typeof data.title === 'string') return data.title;

  return err?.message || fallback;
}
